import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import {
  categoryToSlug,
  slugToCategory,
  getAllCategories,
  getPostsByCategory,
} from '@/utils/blogUtils';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { useCardStagger } from '@/hooks/useCardStagger';
import ReadTimeIndicator from '../blog/components/ReadTimeIndicator';
import { blogPosts } from '@/mocks/blog';

export default function BlogCategoryPage() {
  const { categorySlug = '' } = useParams<{ categorySlug: string }>();

  const categoryName = slugToCategory(categorySlug);
  const posts = getPostsByCategory(categorySlug);
  const allCategories = getAllCategories();

  const maxReadMinutes = Math.max(...blogPosts.map((p) => parseInt(p.readTime, 10) || 0));

  /* Animations */
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: featuredRef, isVisible: featuredVisible } = useScrollAnimation({ threshold: 0.1 });
  const {
    containerRef: gridRef,
    visibleCount,
  } = useCardStagger(Math.max(0, posts.length - 1), { staggerMs: 70 });

  useEffect(() => {
    if (!categoryName) return;

    document.title = `${categoryName} | Blog STIB — Tecnologia de Áudio para Eventos`;

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        `Artigos sobre ${categoryName.toLowerCase()} no blog da STIB. ${posts.length} conteúdos exclusivos sobre tecnologia de áudio, palestra silenciosa e eventos corporativos no Brasil.`
      );
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stib.com.br';
    const schemaId = 'blog-category-schema';
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify([
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${categoryName} — Blog STIB`,
        description: `Artigos sobre ${categoryName.toLowerCase()} da STIB.`,
        url: `${siteUrl}/blog/categoria/${categorySlug}`,
        inLanguage: 'pt-BR',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Blog', item: `${siteUrl}/blog` },
            { '@type': 'ListItem', position: 3, name: categoryName, item: `${siteUrl}/blog/categoria/${categorySlug}` },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: posts.map((p, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          url: `${siteUrl}/blog/${p.slug}`,
          name: p.title,
        })),
      },
    ]);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById(schemaId);
      if (s) s.remove();
    };
  }, [categoryName, categorySlug, posts]);

  // Unknown category — redirect to /blog
  if (!categoryName) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-40 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-6">
            <i className="ri-folder-unknown-line text-3xl text-gray-400"></i>
          </div>
          <h1 className="font-heading font-bold text-3xl text-secondary mb-4">
            Categoria não encontrada
          </h1>
          <p className="font-body text-gray-500 mb-8">
            Essa categoria não existe ou foi removida. Explore todas as categorias abaixo.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-full font-body font-semibold hover:bg-opacity-90 transition-all cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm"></i>
            </div>
            Ir para o Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* ── Hero ── */}
      <section ref={heroRef} className="relative overflow-hidden mt-[72px]">
        <div className="absolute inset-0">
          <div
            className="w-full h-full"
            style={{ background: 'linear-gradient(135deg, #1a3060 0%, #384774 50%, #18c7f4 100%)' }}
          />
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
          />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 py-20">
          {/* Breadcrumb */}
          <nav
            aria-label="Breadcrumb"
            className={`flex items-center gap-2 text-white/50 text-xs font-body mb-8 transition-all duration-500 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <Link to="/" className="hover:text-white/80 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-white/30"></i>
            <Link to="/blog" className="hover:text-white/80 transition-colors cursor-pointer">Blog</Link>
            <i className="ri-arrow-right-s-line text-white/30"></i>
            <span className="text-white/80">{categoryName}</span>
          </nav>

          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className={`transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <span className="inline-block font-body text-xs font-bold uppercase tracking-widest text-primary/80 mb-3">
                Categoria
              </span>
              <h1 className="font-heading font-bold text-5xl text-white leading-tight">
                {categoryName}
              </h1>
            </div>
            <div className={`flex-shrink-0 text-right transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              <p className="font-heading font-bold text-5xl text-white/20 leading-none">
                {posts.length}
              </p>
              <p className="font-body text-sm text-white/60 -mt-1">
                {posts.length === 1 ? 'artigo' : 'artigos'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Category filter pills ── */}
      <div className="border-b border-gray-100 bg-white sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 py-3 overflow-x-auto">
            <Link
              to="/blog"
              className="flex-shrink-0 flex items-center gap-1.5 px-4 py-1.5 rounded-full font-body text-xs font-semibold border border-gray-200 text-gray-500 hover:border-primary hover:text-primary transition-all cursor-pointer whitespace-nowrap"
            >
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-apps-2-line text-xs"></i>
              </div>
              Todos
            </Link>
            {allCategories.map((cat) => {
              const isActive = cat.slug === categorySlug;
              return (
                <Link
                  key={cat.slug}
                  to={`/blog/categoria/${cat.slug}`}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-1.5 rounded-full font-body text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    isActive
                      ? 'bg-primary text-white border border-primary'
                      : 'border border-gray-200 text-gray-500 hover:border-primary hover:text-primary'
                  }`}
                >
                  {cat.name}
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                      isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-400'
                    }`}
                  >
                    {cat.count}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Posts Grid ── */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          {posts.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white border border-gray-100 mx-auto mb-5">
                <i className="ri-file-search-line text-2xl text-gray-300"></i>
              </div>
              <p className="font-heading font-bold text-xl text-secondary mb-2">
                Nenhum artigo nesta categoria ainda
              </p>
              <p className="font-body text-sm text-gray-400 mb-8">
                Em breve teremos conteúdo sobre {categoryName}.
              </p>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full font-body font-semibold text-sm hover:bg-opacity-90 transition-all cursor-pointer whitespace-nowrap"
              >
                Ver todos os artigos
              </Link>
            </div>
          ) : (
            <>
              {/* Featured first post — animated */}
              <div
                ref={featuredRef}
                className={`mb-10 transition-all duration-700 ${featuredVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                <Link to={`/blog/${posts[0].slug}`} className="group cursor-pointer block">
                  <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-primary/25 hover:-translate-y-1 transition-all duration-300 grid grid-cols-1 md:grid-cols-2">
                    <div className="relative h-64 md:h-auto overflow-hidden">
                      <img
                        src={posts[0].image}
                        alt={posts[0].title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="eager"
                        decoding="async"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                      <span className="absolute top-4 left-4 bg-primary text-white px-3 py-1 rounded-full font-body text-xs font-bold uppercase">
                        {posts[0].category}
                      </span>
                      <span className="absolute top-4 right-4 bg-white/90 text-secondary px-3 py-1 rounded-full font-body text-xs font-semibold">
                        Destaque
                      </span>
                    </div>
                    <div className="p-8 flex flex-col justify-center">
                      <p className="font-body text-xs text-gray-400 mb-3">{posts[0].date}</p>
                      <h2 className="font-heading font-bold text-2xl text-secondary group-hover:text-primary transition-colors mb-4 leading-snug">
                        {posts[0].title}
                      </h2>
                      <p className="font-body text-sm text-gray-500 leading-relaxed mb-6 line-clamp-3">
                        {posts[0].excerpt}
                      </p>
                      <ReadTimeIndicator readTime={posts[0].readTime} maxMinutes={maxReadMinutes} />
                      <div className="mt-5 flex items-center gap-1.5 font-body text-sm font-semibold text-primary">
                        Ler artigo completo
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>

              {/* Remaining posts — stagger grid */}
              {posts.length > 1 && (
                <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {posts.slice(1).map((post, index) => (
                    <Link
                      key={post.id}
                      to={`/blog/${post.slug}`}
                      className={`group cursor-pointer transition-all duration-700 ease-out ${
                        index < visibleCount ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                      }`}
                      style={{ transitionDelay: index < visibleCount ? `${(index % 6) * 70}ms` : '0ms' }}
                    >
                      <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-primary/25 hover:-translate-y-1 transition-all duration-300 flex flex-col h-full">
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={post.image}
                            alt={post.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                            decoding="async"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                          <span className="absolute top-3 left-3 bg-primary text-white px-3 py-1 rounded-full font-body text-xs font-bold uppercase">
                            {post.category}
                          </span>
                        </div>
                        <div className="p-6 flex flex-col flex-1">
                          <p className="font-body text-xs text-gray-400 mb-2">{post.date}</p>
                          <h3 className="font-heading font-bold text-base text-secondary group-hover:text-primary transition-colors mb-2 line-clamp-2 leading-snug flex-1">
                            {post.title}
                          </h3>
                          <p className="font-body text-xs text-gray-500 line-clamp-2 mb-4 leading-relaxed">
                            {post.excerpt}
                          </p>
                          <ReadTimeIndicator readTime={post.readTime} maxMinutes={maxReadMinutes} />
                          <div className="mt-4 flex items-center gap-1 font-body text-xs font-semibold text-primary group-hover:gap-2 transition-all">
                            Ler artigo
                            <div className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-arrow-right-line text-xs"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="font-body text-xs font-bold uppercase tracking-widest text-primary mb-3">
            Quer saber mais?
          </p>
          <h2 className="font-heading font-bold text-3xl text-secondary mb-4">
            Fale com um especialista em {categoryName.toLowerCase()}
          </h2>
          <p className="font-body text-gray-500 mb-8">
            A STIB atende todo o Brasil com suporte técnico especializado e equipamentos profissionais.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contato"
              className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-body font-semibold hover:bg-opacity-90 transition-all cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-send-plane-fill text-sm"></i>
              </div>
              Solicitar orçamento
            </Link>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 border border-gray-200 text-secondary px-8 py-4 rounded-full font-body font-semibold hover:border-primary hover:text-primary transition-all cursor-pointer whitespace-nowrap"
            >
              Ver todos os artigos
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
